import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import { translationKeys } from '@gik/inkind-page/i18n/en';
import i18n from '@gik/i18n';
import {
  CopyButton,
  ExternalLink,
  FacebookIcon,
  Input,
  MailIcon,
  MessengerIcon,
  SvgIcon,
  TwitterIcon,
  UI,
} from '@gik/ui';
import { useTranslation } from 'react-i18next';
import {
  copyLinkShareAnalytics,
  createEmailShareUrl,
  createFacebookShareUrl,
  createMessengerShareUrl,
  createTextMessageShareUrl,
  createTwitterShareUrl,
  emailShareAnalytics,
  facebookShareAnalytics,
  messengerShareAnalytics,
  textMessageShareAnalytics,
  twitterShareAnalytics,
} from '@gik/core/utils/ShareUtils';
import TextMessageIcon from '@gik/ui/SocialIcon/TextMessageIcon';
import { Analytics } from '@gik/core/utils';
import { Breakpoint, useBreakpoint } from '@gik/core/hooks/hooks/BreakpointHooks';

export interface ShareInkindModalProps {
  pageShortUrl: string;
  pageTitle: string;
  isOrganizer: boolean;
}

const shareInkindModalBlockName = 'share-inkind-modal';
const bem = bemBlock(shareInkindModalBlockName);

function ShareOptions(props: React.PropsWithChildren<{}>): JSX.Element {
  return <>{props.children}</>;
}

interface ShareOptionProps {
  mobileOnly?: boolean;
  url?: string;
  title?: string;
  icon: React.ReactNode;
  customContent?: React.ReactNode;

  onClick?(): void;
}

function ShareOption({ mobileOnly, customContent, url, icon, title, onClick }: ShareOptionProps): React.ReactElement {
  const isSmDown = useBreakpoint(Breakpoint.SM_DOWN);

  return isSmDown || !mobileOnly ? (
    <>
      {customContent ? (
        <div className={bem('share-option-custom')}>
          <div>{icon}</div>
          {customContent}
        </div>
      ) : (
        <div>
          <ExternalLink href={url} onClick={onClick}>
            <div className={bem('share-option')}>
              {icon}
              <p>{title}</p>
            </div>
          </ExternalLink>
        </div>
      )}
      <hr />
    </>
  ) : null;
}

function ShareInkindModalContent({ pageTitle, pageShortUrl }: ShareInkindModalProps): React.ReactElement {
  const { t } = useTranslation();
  const shareBody = encodeURIComponent(`${pageTitle}'s Give InKind Page`);
  const isSmDown = useBreakpoint(Breakpoint.SM_DOWN);

  return (
    <>
      <p className={bem('content-title')}>{t(translationKeys.ShareInkindModalContentTitle)}</p>
      <p className={bem('description')}>{t(translationKeys.ShareInkindModalDescription)}</p>
      <ShareOptions>
        <ShareOption
          url={createFacebookShareUrl(pageShortUrl)}
          title="Facebook"
          onClick={() => facebookShareAnalytics()}
          icon={<FacebookIcon />}
        />
        <ShareOption
          url={createMessengerShareUrl(pageShortUrl, isSmDown)}
          title="Messenger"
          onClick={() => messengerShareAnalytics()}
          icon={<MessengerIcon />}
        />
        <ShareOption
          url={createTwitterShareUrl(pageShortUrl)}
          title="Twitter"
          onClick={() => twitterShareAnalytics()}
          icon={<TwitterIcon />}
        />
        <ShareOption
          url={createEmailShareUrl(pageShortUrl, shareBody)}
          title="Email"
          onClick={() => emailShareAnalytics()}
          icon={<MailIcon />}
        />
        <ShareOption
          mobileOnly
          url={createTextMessageShareUrl(shareBody + encodeURIComponent(': ') + pageShortUrl)}
          title="Text Message"
          onClick={() => textMessageShareAnalytics()}
          icon={<TextMessageIcon />}
        />
        <ShareOption
          icon={<SvgIcon name="LinkOutline" size="xl" />}
          customContent={
            <>
              <Input value={pageShortUrl} readOnly selectOnFocus />
              <CopyButton
                variant="primary"
                textToCopy={pageShortUrl}
                title="copy"
                size="base"
                notificationText="Link copied"
                onClick={() => copyLinkShareAnalytics()}
              />
            </>
          }
        />
      </ShareOptions>
    </>
  );
}

export function openShareInkindModal(
  props: ShareInkindModalProps,
  initiatedOn: 'inkindPage' | 'profilePage' | 'supportersPage',
  inkindPageUrl: string
): void {
  Analytics.fireEvent('OpenShareInkindModal', {
    initiatedOn,
    isOrganizer: props.isOrganizer.toString(),
    inkindPageUrl,
  });
  UI.dialog(<ShareInkindModalContent {...props} />, {
    title: i18n.t(translationKeys.ShareInkindModalTitle),
    closable: true,
    className: bem(),
  });
}
